import apollo from "@/apollo.js"
import gql from "graphql-tag"

import { setScriptEngineAttrs, setAccountTimezone, setDefaultTeam, setAccountChatBriefing, setAccountChatReports, setChatMemoryEnabled, setAccountRefreshTokenExpiryHours, setAccountAdminConsoleToken, setOidcSso, performReindex, accountFeatureToggles, setAccountFeature } from "./provider/accounts"
import { getToken, verifyTOTP, getSsoConfig, exchangeSsoSession } from "./provider/authentication"
import { getTasks, executeTask, createTaskTemplate, updateTaskTemplate, deleteTaskTemplate, executeBucketRunTask, executeSelectedCaseRunTask } from "./provider/tasks"
import { createTasklistTemplate, updateTasklistTemplate, deleteTasklistTemplate } from "./provider/tasklists"
import { createCaseTasklist, updateCaseTasklist, deleteCaseTasklist } from "./provider/case_tasklists"
import { createCaseTask, updateCaseTask, deleteCaseTask, executeCaseTask } from "./provider/case_tasks"
import { createTaskTemplateParameter, updateTaskTemplateParameter, deleteTaskTemplateParameter } from "./provider/taskparameters"
import { createCase, deleteCase, deleteCases, deleteBucketCases, deleteSearchCases, updateCase, addCaseMember, removeCaseMember, findCase, findCaseAttachments, findCaseSummary, findRelatedCases, getCaseSearch, getGlobalSearch, getCaseTypeSearch, subscribeToCaseEvents, unsubscribeFromCaseEvents, createCaseNote, updateCaseNote, deleteCaseNote, resolveTaskFailures, enterCase, exitCase, subscribeToCasePresence, unsubscribeFromCasePresence, subscribeToBucketPresence, unsubscribeFromBucketPresence, getCaseConversationsPage } from "./provider/cases"
import { findUser, findCurrentUser, createUser, deleteUser, updateUser, updateUserWithPassword, setupMFA, disableMFA, requestForgotPassword, resetPassword, updateUserPreferences, clearAllNotifications, clearNotification, reloadBookmarks, refreshNotifications, subscribeToNotifications, unsubscribeFromNotifications, refreshRecentCases, reloadAccount, reloadAssignedTasks, reloadTeamsAndUsers, reloadCaseTypes, searchSavedSearch, reloadSavedSearches, reloadSavedSearchesTotals, createSavedSearch, deleteSavedSearch, renameSavedSearch } from "./provider/users"
import { createSummaryDisplayField, updateSummaryDisplayField, summariseCases, deleteSummaryDisplayField } from "./provider/summary_display_fields"
import { createCaseState, updateCaseState, deleteCaseState } from "./provider/case_states"
import { grantPermission, revokePermission, grantFieldsetPermission, revokeFieldsetPermission, grantTasklistPermission, revokeTasklistPermission } from "./provider/permissions"
import { createCaseConversation, attachConversationToCase, removeConversationFromCase, archiveMessage, markConversationAsRead, replyCaseConversation, forwardMessage, deleteMessage, accountRecipients, resendConversationMessages } from "./provider/conversations"
import { createCaseType, updateCaseType, updateExistingCases, updateExistingTasks, deleteCaseType, importAtomicData, importTabularData, reindexDocuments, reindexCaseType } from "./provider/casetypes"
import { createDirectRelationship, updateDirectRelationship, deleteDirectRelationship, createDirectRelationshipDisplayField, deleteDirectRelationshipDisplayField } from "./provider/relationships"
import { createEmailTemplate, updateEmailTemplate, deleteEmailTemplate, renderEmailTemplate } from "./provider/email_templates"
import { systemEmailTemplates, updateSystemEmailTemplate, resetSystemEmailTemplate } from "./provider/system_emails"
import { createEmailAccount, updateEmailAccount, deleteEmailAccount, setupMailgunDomain } from "./provider/email_accounts"
import { createScript, updateScript, executeScript, getScriptExecutionPhase, deleteScript } from "./provider/scripts"
import { findCaseTimeline, getAuditLog, getUserTimeline } from "./provider/timeline"
import { findBucket, addBucketMember, removeBucketMember, findOrderedBucket, findBucketMapPoints, createBucket, updateBucketOwner, updateBucket, exportBucket, deleteBucket, getBucketsByCaseType } from "./provider/buckets"
import { createBucketGroup, updateBucketGroup, deleteBucketGroup } from "./provider/bucket_groups"
import { createTeam, updateTeam, deleteTeam, createTeamMembership, deleteTeamMembership } from "./provider/teams"
import { createBucketDisplayFields, updateBucketDisplayFields, createBucketSortFields, deleteBucketSortFields, createBucketFilterFields, updateBucketFilterFields } from "./provider/bucket_fields"
import { createBookmark, deleteBookmark } from "./provider/bookmarks"
import { createServiceLevelCriteria, updateServiceLevelCriteria, deleteServiceLevelCriteria } from "./provider/serviceLevelCriteria"
import { createScreenLayoutItem, updateScreenLayoutItem, deleteScreenLayoutItem } from "./provider/screenLayouts"
import { deleteFileAttachment, renameFileAttachment, duplicateFileAttachment, moveFileAttachment, hideFileAttachment, showFileAttachment } from "./provider/case_documents"
import { getJobsPage, getJobLogs, getJobTypes } from "./provider/jobs"
import { getMetrics, updateMetric, createCasePropertyMetric, createStateTransitionMetric, createBucketMetric, deleteMetric, createWidget, deleteWidget, getWidgets, updateWidget, reorderWidgets, createDashboardSection, updateDashboardSection, deleteDashboardSection } from "./provider/metrics"
import { createTemplatedFolder, deleteTemplatedFolder, updateTemplatedFolder, getTemplatedFolders, getCaseFolders, getBreadcrumbFolders } from "./provider/case_folders"

import { getAllChatSessions, getChatSession, createChatSession, deleteChatSession, updateChatSession, createChatMessage, subscribeToChatSession, unsubscribeFromChatSession, subscribeToChatMessages, unsubscribeFromChatMessages, reconnectChatSubscriptions, upVoteMessage, downVoteMessage, getChatMessagesForSession, addChatMessageStatus } from "./provider/chat"

export const provider = {
  getMetrics,
  updateMetric,
  createCasePropertyMetric,
  createStateTransitionMetric,
  createBucketMetric,
  deleteMetric,
  createWidget,
  deleteWidget,
  getWidgets,
  updateWidget,
  reorderWidgets,
  createDashboardSection,
  updateDashboardSection,
  deleteDashboardSection,
  requestForgotPassword,
  resetPassword,
  setScriptEngineAttrs,
  setAccountTimezone,
  setDefaultTeam,
  setAccountChatBriefing,
  setAccountChatReports,
  setChatMemoryEnabled,
  accountFeatureToggles,
  setAccountFeature,
  setAccountRefreshTokenExpiryHours,
  setAccountAdminConsoleToken,
  setOidcSso,
  performReindex,
  getJobLogs,
  getJobTypes,
  getToken,
  verifyTOTP,
  getSsoConfig,
  exchangeSsoSession,
  getTasks,
  executeTask,
  createTaskTemplate,
  updateTaskTemplate,
  deleteTaskTemplate,
  executeBucketRunTask,
  executeSelectedCaseRunTask,
  createTasklistTemplate,
  updateTasklistTemplate,
  deleteTasklistTemplate,
  createTaskTemplateParameter,
  updateTaskTemplateParameter,
  deleteTaskTemplateParameter,
  createCaseTasklist,
  updateCaseTasklist,
  deleteCaseTasklist,
  createCaseTask,
  updateCaseTask,
  deleteCaseTask,
  executeCaseTask,
  createCase,
  deleteCase,
  deleteCases,
  deleteBucketCases,
  deleteSearchCases,
  updateCase,
  addCaseMember,
  removeCaseMember,
  findCase,
  getCaseConversationsPage,
  findCaseAttachments,
  findCaseSummary,
  findRelatedCases,
  getCaseSearch,
  getGlobalSearch,
  getCaseTypeSearch,
  subscribeToCaseEvents,
  unsubscribeFromCaseEvents,
  createCaseNote,
  updateCaseNote,
  deleteCaseNote,
  resolveTaskFailures,
  enterCase,
  subscribeToBucketPresence,
  unsubscribeFromBucketPresence,
  exitCase,
  subscribeToCasePresence,
  unsubscribeFromCasePresence,
  findUser,
  findCurrentUser,
  createUser,
  deleteUser,
  updateUser,
  updateUserWithPassword,
  setupMFA,
  disableMFA,
  updateUserPreferences,
  clearAllNotifications,
  clearNotification,
  reloadBookmarks,
  refreshNotifications,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  refreshRecentCases,
  reloadAccount,
  reloadAssignedTasks,
  reloadTeamsAndUsers,
  reloadCaseTypes,
  searchSavedSearch,
  reloadSavedSearches,
  reloadSavedSearchesTotals,
  createSavedSearch,
  deleteSavedSearch,
  renameSavedSearch,
  createSummaryDisplayField,
  updateSummaryDisplayField,
  summariseCases,
  deleteSummaryDisplayField,
  createCaseState,
  updateCaseState,
  deleteCaseState,
  grantPermission,
  revokePermission,
  grantFieldsetPermission,
  revokeFieldsetPermission,
  grantTasklistPermission,
  revokeTasklistPermission,
  createCaseConversation,
  attachConversationToCase,
  removeConversationFromCase,
  archiveMessage,
  markConversationAsRead,
  replyCaseConversation,
  forwardMessage,
  deleteMessage,
  accountRecipients,
  resendConversationMessages,
  createCaseType,
  updateCaseType,
  updateExistingCases,
  updateExistingTasks,
  deleteCaseType,
  importAtomicData,
  importTabularData,
  reindexDocuments,
  reindexCaseType,
  createDirectRelationship,
  updateDirectRelationship,
  deleteDirectRelationship,
  createDirectRelationshipDisplayField,
  deleteDirectRelationshipDisplayField,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  renderEmailTemplate,
  systemEmailTemplates,
  updateSystemEmailTemplate,
  resetSystemEmailTemplate,
  createEmailAccount,
  updateEmailAccount,
  deleteEmailAccount,
  setupMailgunDomain,
  createScript,
  updateScript,
  executeScript,
  getScriptExecutionPhase,
  deleteScript,
  findCaseTimeline,
  getUserTimeline,
  getAuditLog,
  findBucket,
  addBucketMember,
  removeBucketMember,
  findOrderedBucket,
  findBucketMapPoints,
  createBucket,
  updateBucketOwner,
  updateBucket,
  exportBucket,
  deleteBucket,
  createBucketGroup,
  updateBucketGroup,
  deleteBucketGroup,
  createTeamMembership,
  deleteTeamMembership,
  createTeam,
  updateTeam,
  deleteTeam,
  createBucketDisplayFields,
  updateBucketDisplayFields,
  createBucketSortFields,
  deleteBucketSortFields,
  createBucketFilterFields,
  updateBucketFilterFields,
  getBucketsByCaseType,
  createBookmark,
  deleteBookmark,
  createServiceLevelCriteria,
  updateServiceLevelCriteria,
  deleteServiceLevelCriteria,
  createScreenLayoutItem,
  updateScreenLayoutItem,
  deleteScreenLayoutItem,
  deleteFileAttachment,
  renameFileAttachment,
  duplicateFileAttachment,
  moveFileAttachment,
  hideFileAttachment,
  showFileAttachment,
  getJobsPage,
  getTemplatedFolders,
  createTemplatedFolder,
  deleteTemplatedFolder,
  updateTemplatedFolder,
  getCaseFolders,
  getBreadcrumbFolders,
  getAllChatSessions,
  getChatSession,
  createChatSession,
  deleteChatSession,
  updateChatSession,
  createChatMessage,
  subscribeToChatSession,
  unsubscribeFromChatSession,
  subscribeToChatMessages,
  unsubscribeFromChatMessages,
  reconnectChatSubscriptions,
  upVoteMessage,
  downVoteMessage,
  getChatMessagesForSession,
  addChatMessageStatus
}

provider.updateQuestions = async (data) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateQuestions($payload: JSON!) {
          updateQuestions(payload: $payload) { message error }
        }
      `),
    variables: { payload: data },
  })
  return response.data.updateQuestions
}

provider.updateQuestionSets = async (data) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateQuestionSets($payload: JSON!) {
          updateQuestionSets(payload: $payload) { message error }
        }`),
    variables: { payload: data },
  })
  return response.data.updateQuestionSets
}
